import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DAY_END_MIN,
  DAY_START_MIN,
  PX_PER_MIN,
  WEEKDAY_NAMES,
  addDays,
  minutesToHHMM,
  parseDateKey,
  toDateKey,
  toWeekKey,
  weekKeyToMonday,
  type ScheduleLesson,
} from '@skolveckan/shared';
import { hourMarks } from '../components/DayTimeline';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { EmptyState } from '../components/EmptyState';
import { LoginLinks } from '../components/LoginLinks';
import { IconCheck, IconChevronLeft, IconChevronRight, IconPlus } from '../components/Icons';
import { useApi } from '../hooks/useApi';
import { useNow } from '../hooks/useNow';
import { ApiError, api } from '../lib/api';
import { subjectColors } from '../lib/subjects';

/**
 * Lärarens överblick. Varje lektion visar om den har innehåll eller inte —
 * det är den enda frågan man har när man sitter med planeringen på söndagen.
 */
export function AdminSchedulePage() {
  const now = useNow(60_000);
  const today = toDateKey(now);
  const navigate = useNavigate();

  const [weekKey, setWeekKey] = useState(() => toWeekKey(now));
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [dragTemplate, setDragTemplate] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [copyOpen, setCopyOpen] = useState(false);
  const [note, setNote] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const week = useApi(() => api.week(weekKey), [weekKey]);
  const coverage = useApi(() => api.coverage(weekKey), [weekKey]);
  const templates = useApi(() => api.templates(), []);

  const height = (DAY_END_MIN - DAY_START_MIN) * PX_PER_MIN;
  const days = week.data?.days ?? [];

  function step(delta: number) {
    setWeekKey(toWeekKey(addDays(weekKeyToMonday(weekKey), delta * 7)));
    setNote(null);
  }

  const previousWeekKey = toWeekKey(addDays(weekKeyToMonday(weekKey), -7));

  function refresh() {
    week.reload();
    coverage.reload();
  }

  async function copyPreviousWeek() {
    setCopyOpen(false);
    setNote(null);
    try {
      const res = await api.copyWeek(previousWeekKey, weekKey);
      refresh();
      // "Inget att kopiera" bara när det verkligen inte fanns något. Med
      // skipped > 0 fanns det innehåll — det lämnades bara orört, och det är
      // en helt annan sak att berätta för den som just tryckte på knappen.
      const parts: string[] = [];
      if (res.copied) parts.push(`${res.copied} lektioner kopierade`);
      if (res.skipped) parts.push(`${res.skipped} hade redan innehåll och lämnades orörda`);
      if (res.missing) parts.push(`${res.missing} saknar motsvarighet i schemat`);
      setNote({
        kind: 'ok',
        text: parts.length
          ? parts.join(', ') + '.'
          : 'Inget att kopiera — förra veckan har inget ifyllt innehåll.',
      });
    } catch (err) {
      setNote({
        kind: 'error',
        text: err instanceof ApiError ? err.message : 'Kunde inte kopiera veckan.',
      });
    }
  }

  /** Drag-och-släpp fyller lektionen direkt. Klickvägen finns kvar för den som
   *  vill granska först — och för den som inte har mus. */
  async function dropTemplateOn(lessonId: string, templateId: string) {
    setNote(null);
    try {
      await api.applyTemplate(lessonId, templateId, { overwrite: false, commit: true });
      refresh();
      setNote({ kind: 'ok', text: 'Mallen lades in. Öppna lektionen för att justera.' });
    } catch (err) {
      setNote({
        kind: 'error',
        text: err instanceof ApiError ? err.message : 'Kunde inte lägga in mallen.',
      });
    }
  }

  return (
    <>
      <header className="page__head">
        <h1>Planera veckan</h1>
        <span className="chip chip--accent">
          Klass {week.data?.className ?? '—'} · vecka {weekKey.split('-W')[1]}
        </span>
        <div className="grow" />
        <button
          type="button"
          className="button button--ghost"
          onClick={() => setCopyOpen(true)}
        >
          Kopiera förra veckan
        </button>
        <button
          type="button"
          className="button button--ghost button--icon"
          onClick={() => step(-1)}
          aria-label="Föregående vecka"
        >
          <IconChevronLeft />
        </button>
        <button
          type="button"
          className="button button--ghost button--icon"
          onClick={() => step(1)}
          aria-label="Nästa vecka"
        >
          <IconChevronRight />
        </button>
      </header>

      {note && (
        <p className={`formnote formnote--${note.kind}`} role="status">
          {note.text}
        </p>
      )}

      <div className="admin__columns">
        <aside className="card panel templates">
          <div className="row">
            <span className="label">Mallar</span>
            <span className="grow" />
            <button type="button" className="iconbutton" aria-label="Ny mall">
              <IconPlus size={13} />
            </button>
          </div>
          <p className="muted">
            Välj en mall och klicka sedan på lektionen du vill fylla i.
          </p>

          {(templates.data?.templates ?? []).map((template) => {
            const active = selectedTemplate === template.id;
            return (
              <button
                key={template.id}
                type="button"
                className={`templatecard${active ? ' templatecard--active' : ''}`}
                aria-pressed={active}
                draggable
                onDragStart={(e) => {
                  setDragTemplate(template.id);
                  e.dataTransfer.effectAllowed = 'copy';
                  e.dataTransfer.setData('text/plain', template.id);
                }}
                onDragEnd={() => {
                  setDragTemplate(null);
                  setDropTarget(null);
                }}
                onClick={() => setSelectedTemplate(active ? null : template.id)}
              >
                <span className="templatecard__name">{template.name}</span>
                <span className="templatecard__desc">{template.description}</span>
                <span className="chiprow">
                  <span className="chip">{template.payload.steps.length} steg</span>
                  {template.payload.links.length > 0 && (
                    <span className="chip">{template.payload.links.length} länkar</span>
                  )}
                </span>
              </button>
            );
          })}

          {templates.data?.templates.length === 0 && (
            <EmptyState
              title="Inga mallar än"
              body="Fyll i en lektion och spara den som mall, så slipper du göra om jobbet."
            />
          )}

          {/* Ingen classId: sidan i övrigt använder också serverns standardklass. */}
          <LoginLinks />

          <div className="grow" />
          {coverage.data && (
            <div className="coverage">
              <span className="coverage__num">
                {coverage.data.filled} av {coverage.data.total}
              </span>
              <span className="muted">lektioner ifyllda</span>
              <span className="progress">
                <span
                  className="progress__fill"
                  style={{
                    width: `${coverage.data.total ? Math.round((coverage.data.filled / coverage.data.total) * 100) : 0}%`,
                  }}
                />
              </span>
            </div>
          )}
        </aside>

        <section className="card panel panel--grow">
          <div className="weekgrid__header">
            <div className="weekgrid__gutter" />
            {days.map((day) => (
              <div
                key={day.date}
                className={`weekgrid__dayname${day.date === today ? ' weekgrid__dayname--today' : ''}`}
              >
                <span>{capitalize(WEEKDAY_NAMES[day.weekday - 1] ?? '')}</span>
                <span className="weekgrid__daydate">{formatShort(day.date)}</span>
              </div>
            ))}
          </div>

          <div className="weekgrid" style={{ height }}>
            <div className="weekgrid__gutter weekgrid__gutter--times">
              {hourMarks().map((m) => (
                <span
                  key={m}
                  className="timeline__hourlabel timeline__hourlabel--absolute"
                  style={{ top: (m - DAY_START_MIN) * PX_PER_MIN - 7 }}
                >
                  {String(Math.floor(m / 60)).padStart(2, '0')}
                </span>
              ))}
            </div>

            {days.map((day) => (
              <div
                key={day.date}
                className={`weekgrid__col${day.date === today ? ' weekgrid__col--today' : ''}`}
              >
                {day.lessons.map((lesson) => (
                  <AdminBlock
                    key={lesson.id}
                    lesson={lesson}
                    dropActive={dropTarget === lesson.id}
                    canDrop={dragTemplate !== null}
                    onDragOver={() => setDropTarget(lesson.id)}
                    onDragLeave={() => setDropTarget((c) => (c === lesson.id ? null : c))}
                    onDrop={(templateId) => {
                      setDropTarget(null);
                      setDragTemplate(null);
                      void dropTemplateOn(lesson.id, templateId);
                    }}
                    onOpen={() =>
                      navigate(
                        `/admin/lektion/${lesson.id}${selectedTemplate ? `?mall=${selectedTemplate}` : ''}`,
                      )
                    }
                  />
                ))}
              </div>
            ))}
          </div>
        </section>
      </div>

      <ConfirmDialog
        open={copyOpen}
        title={`Kopiera vecka ${previousWeekKey.split('-W')[1]} hit?`}
        body={
          'Lektioner som redan har innehåll lämnas orörda. Uppgifter och prov följer ' +
          'inte med — deras inlämningsdatum hör till sin egen vecka.'
        }
        cancelLabel="Avbryt"
        confirmLabel="Kopiera"
        onCancel={() => setCopyOpen(false)}
        onConfirm={() => void copyPreviousWeek()}
      />
    </>
  );
}

interface BlockProps {
  lesson: ScheduleLesson;
  onOpen: () => void;
  canDrop?: boolean;
  dropActive?: boolean;
  onDragOver?: () => void;
  onDragLeave?: () => void;
  onDrop?: (templateId: string) => void;
}

function AdminBlock({
  lesson,
  onOpen,
  canDrop,
  dropActive,
  onDragOver,
  onDragLeave,
  onDrop,
}: BlockProps) {
  const top = (lesson.startMin - DAY_START_MIN) * PX_PER_MIN;
  const height = Math.max((lesson.endMin - lesson.startMin) * PX_PER_MIN - 2, 14);
  const colors = subjectColors(lesson.subject?.colorKey ?? 'break');

  if (lesson.kind !== 'lesson') {
    return (
      <div className="block block--break" style={{ top, height }}>
        <span className="block__breaklabel">
          {lesson.kind === 'lunch' ? 'Lunch' : 'Rast'}
        </span>
      </div>
    );
  }

  const filled = lesson.hasContent;

  return (
    <button
      type="button"
      className={
        `adminblock${filled ? '' : ' adminblock--empty'}` +
        (canDrop ? ' adminblock--droppable' : '') +
        (dropActive ? ' adminblock--dropover' : '')
      }
      style={{
        top,
        height,
        background: filled ? colors.fill : 'var(--surface)',
        borderLeftColor: filled ? colors.line : 'transparent',
      }}
      onDragOver={(e) => {
        if (!canDrop) return;
        // preventDefault är det som gör elementet till en släppzon — utan den
        // avvisar webbläsaren släppet och inget onDrop kommer.
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        onDragOver?.();
      }}
      onDragLeave={() => onDragLeave?.()}
      onDrop={(e) => {
        e.preventDefault();
        const templateId = e.dataTransfer.getData('text/plain');
        if (templateId) onDrop?.(templateId);
      }}
      onClick={onOpen}
    >
      <span className="adminblock__subject" style={{ color: filled ? colors.ink : 'var(--ink-muted)' }}>
        {lesson.subject?.name ?? 'Lektion'}
      </span>
      <span className="adminblock__time">
        {minutesToHHMM(lesson.startMin)}
        {lesson.room ? ` · ${lesson.room.replace('Sal ', '')}` : ''}
      </span>
      <span className="grow" />
      {filled ? (
        <span className="adminblock__ok">
          <IconCheck size={10} />
          Klar
          {lesson.assignmentCount > 0 ? ` · ${lesson.assignmentCount} uppg.` : ''}
        </span>
      ) : (
        <span className="adminblock__missing">Saknar innehåll</span>
      )}
    </button>
  );
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatShort(dateKey: string): string {
  const d = parseDateKey(dateKey);
  return `${d.getDate()}/${d.getMonth() + 1}`;
}
